// Component schemas and examples for LLM assistant messages
// These can be safely imported in the frontend

import { z } from "zod/v4";

// Chart Data Schema (shared by Bar and Line charts)
const BaseChartConfigSchema = z.object({
  title: z.string().describe("Descriptive chart title"),
  xAxisLabel: z
    .string()
    .optional()
    .describe("Label for the horizontal (x) axis"),
  yAxisLabel: z.string().optional().describe("Label for the vertical (y) axis"),
});

// Named palette for manual bar coloring on bar charts. Maps to the
// design system's CHART_COLORS on the frontend.
export const CHART_BAR_COLORS = [
  "blue",
  "yellow",
  "purple",
  "green",
  "orange",
  "brown",
  "pink",
  "red",
  "gray",
] as const;

const BaseChartPointSchema = z.object({
  label: z.string().describe("Category name (e.g., '18-24', 'Product A')"),
  value: z
    .number()
    .describe("Numeric value (percentages as decimals: 0.15 for 15%)"),
  tooltip: z
    .string()
    .describe(
      "Context for tooltip (e.g., '18-24: 15% CTR • 1.2M impressions')",
    ),
});

// Only bar charts honor a manual per-point color, so only the bar point
// schema advertises it — the prompt-embedded line chart schema must not
// offer a field its renderer ignores.
const BarChartPointSchema = BaseChartPointSchema.extend({
  color: z
    .enum(CHART_BAR_COLORS)
    .optional()
    // A hallucinated color name must not fail the whole chart parse
    // (the FE renders a parse failure as a chart-wide error alert);
    // degrade to the automatic palette instead.
    .catch(undefined)
    .describe(
      "Optional manual bar color. If not specified, bars are automatically assigned different colors. Set it to visually group related bars in the same color.",
    ),
});

const chartSeriesSchema = <Point extends z.ZodType>(pointSchema: Point) =>
  z.array(
    z.object({
      name: z.string().describe("Series name for legend"),
      data: z.array(pointSchema),
    }),
  );

export const BarChartDataSchema = z.object({
  config: BaseChartConfigSchema.extend({
    orientation: z
      .enum(["vertical", "horizontal"])
      .optional()
      .describe(
        "Chart orientation: 'vertical' for traditional column chart (default), 'horizontal' for horizontal bar chart (better for rankings and long labels)",
      ),
    percentage: z
      .boolean()
      .optional()
      // A hallucinated non-boolean value must not fail the whole chart
      // parse; degrade to a standard value axis instead.
      .catch(undefined)
      .describe(
        "Set to true when every value is a fraction between 0 and 1 representing a percentage. Values display as percentages instead of raw numbers.",
      ),
  }),
  series: chartSeriesSchema(BarChartPointSchema),
});
export type BarChartData = z.infer<typeof BarChartDataSchema>;

export const BAR_CHART_EXAMPLE: BarChartData = {
  config: {
    title: "CPA by Age Group",
    yAxisLabel: "Cost per Acquisition ($)",
  },
  series: [
    {
      name: "Performance",
      data: [
        {
          label: "18-24",
          value: 12.5,
          tooltip: "18-24: $12.50 CPA • 450 conversions",
        },
        {
          label: "25-34",
          value: 18.75,
          tooltip: "25-34: $18.75 CPA • 1.2K conversions",
        },
        {
          label: "35-44",
          value: 22.3,
          tooltip: "35-44: $22.30 CPA • 890 conversions",
        },
      ],
    },
  ],
};

export const BAR_CHART_HORIZONTAL_EXAMPLE: BarChartData = {
  config: {
    title: "Top 5 Campaigns by CTR",
    xAxisLabel: "Click-Through Rate (%)",
    orientation: "horizontal",
    percentage: true,
  },
  series: [
    {
      name: "Performance",
      data: [
        {
          label: "Spring Sale - Email Campaign 2024",
          value: 0.189,
          tooltip: "Spring Sale Email: 18.9% CTR • 2.5M impressions",
        },
        {
          label: "Product Launch - Social Media",
          value: 0.175,
          tooltip: "Product Launch Social: 17.5% CTR • 1.8M impressions",
        },
        {
          label: "Holiday Promotion - Newsletter",
          value: 0.162,
          tooltip: "Holiday Newsletter: 16.2% CTR • 3.1M impressions",
        },
        {
          label: "Brand Awareness - Display Ads",
          value: 0.148,
          tooltip: "Brand Display: 14.8% CTR • 4.2M impressions",
        },
        {
          label: "Retargeting - Dynamic Ads",
          value: 0.135,
          tooltip: "Retargeting Dynamic: 13.5% CTR • 1.5M impressions",
        },
      ],
    },
  ],
};

export const LineChartDataSchema = z.object({
  config: BaseChartConfigSchema,
  series: chartSeriesSchema(BaseChartPointSchema),
});
export type LineChartData = z.infer<typeof LineChartDataSchema>;

export const LINE_CHART_EXAMPLE: LineChartData = {
  config: {
    title: "Revenue Trend by Channel",
    yAxisLabel: "Revenue ($)",
  },
  series: [
    {
      name: "Email",
      data: [
        {
          label: "Week 1",
          value: 12500,
          tooltip: "Email Week 1: $12.5K revenue",
        },
        {
          label: "Week 2",
          value: 15200,
          tooltip: "Email Week 2: $15.2K revenue",
        },
        {
          label: "Week 3",
          value: 18900,
          tooltip: "Email Week 3: $18.9K revenue",
        },
      ],
    },
    {
      name: "SMS",
      data: [
        {
          label: "Week 1",
          value: 8400,
          tooltip: "SMS Week 1: $8.4K revenue",
        },
        {
          label: "Week 2",
          value: 9100,
          tooltip: "SMS Week 2: $9.1K revenue",
        },
        {
          label: "Week 3",
          value: 11200,
          tooltip: "SMS Week 3: $11.2K revenue",
        },
      ],
    },
  ],
};

export const HeatmapDataSchema = z.object({
  config: z.object({
    title: z.string().describe("Descriptive heatmap title"),
    metric: z
      .string()
      .describe("Metric being displayed (e.g., 'CTR (%)', 'CPA ($)')"),
    rowLabel: z
      .string()
      .describe("Label for row dimension (e.g., 'Age Group', 'Product')"),
    columnLabel: z
      .string()
      .describe("Label for column dimension (e.g., 'Gender', 'Campaign Type')"),
  }),
  rows: z.array(
    z.object({
      label: z
        .string()
        .describe("Row category name (e.g., '18-24', 'Sneakers')"),
      cells: z.array(
        z.object({
          value: z.number().describe("Numeric metric value"),
        }),
      ),
    }),
  ),
  columns: z.array(z.string()).describe("Column category names"),
});

export type HeatmapData = z.infer<typeof HeatmapDataSchema>;

export const HEATMAP_EXAMPLE: HeatmapData = {
  config: {
    title: "CTR by Age Group x Gender",
    metric: "CTR (%)",
    rowLabel: "Age Group",
    columnLabel: "Gender",
  },
  rows: [
    {
      label: "18-24",
      cells: [{ value: 12.5 }, { value: 10.8 }],
    },
    {
      label: "25-34",
      cells: [{ value: 15.2 }, { value: 13.7 }],
    },
  ],
  columns: ["Male", "Female"],
};

export const FunnelDataSchema = z.object({
  config: z.object({
    title: z.string().describe("Descriptive funnel title"),
    conversionMetric: z
      .string()
      .describe(
        "What's being measured (e.g., 'Users', 'Sessions', 'Visitors')",
      ),
  }),
  series: z
    .array(
      z.object({
        name: z
          .string()
          .describe(
            "Series name for comparison (e.g., 'Male', 'Female', 'Control', 'Variant')",
          ),
        steps: z
          .array(
            z.object({
              name: z
                .string()
                .describe(
                  "Step name (e.g., 'Homepage Visit', 'Add to Cart', 'Purchase')",
                ),
              value: z
                .number()
                .describe("Absolute count at this step (e.g., 10000 users)"),
              conversionRate: z
                .number()
                .describe(
                  "Conversion rate from previous step as decimal (0.75 for 75%). First step should be 1.0",
                ),
            }),
          )
          .min(2)
          .describe("Minimum 2 steps required for a funnel"),
      }),
    )
    .min(1)
    .describe("At least one series required. Multiple series for comparison."),
});

export type FunnelData = z.infer<typeof FunnelDataSchema>;

export const FUNNEL_SINGLE_SERIES_EXAMPLE: FunnelData = {
  config: {
    title: "E-commerce Checkout Funnel",
    conversionMetric: "Users",
  },
  series: [
    {
      name: "All Users",
      steps: [
        { name: "Homepage", value: 10000, conversionRate: 1.0 },
        { name: "Product Page", value: 7500, conversionRate: 0.75 },
        { name: "Add to Cart", value: 3000, conversionRate: 0.4 },
        { name: "Checkout", value: 1500, conversionRate: 0.5 },
        { name: "Purchase", value: 1200, conversionRate: 0.8 },
      ],
    },
  ],
};

export const FUNNEL_MULTI_SERIES_EXAMPLE: FunnelData = {
  config: {
    title: "Checkout Funnel by Gender",
    conversionMetric: "Users",
  },
  series: [
    {
      name: "Male",
      steps: [
        { name: "Homepage", value: 5000, conversionRate: 1.0 },
        { name: "Product Page", value: 4000, conversionRate: 0.8 },
        { name: "Add to Cart", value: 1800, conversionRate: 0.45 },
        { name: "Purchase", value: 900, conversionRate: 0.5 },
      ],
    },
    {
      name: "Female",
      steps: [
        { name: "Homepage", value: 5000, conversionRate: 1.0 },
        { name: "Product Page", value: 3500, conversionRate: 0.7 },
        { name: "Add to Cart", value: 1200, conversionRate: 0.34 },
        { name: "Purchase", value: 720, conversionRate: 0.6 },
      ],
    },
  ],
};
